import { mapValues } from "lodash";
import { isNotNullNorUndefined } from "@silo/utils";
import {
  Provider,
  JsonRpcProvider,
  FallbackProvider,
} from "@ethersproject/providers";
import { Contract } from "ethers";
import { QueryKey } from "@tanstack/react-query";

export const queryKeyHashFn = (queryKey: QueryKey) =>
  JSON.stringify(queryKey.map(prepareToSerialize)).replace(/\\/g, "");

const prepareToSerialize = (value: unknown): unknown => {
  if (typeof value === "bigint") {
    return value.toString();
  }

  if (value instanceof Contract) {
    return prepareContract(value);
  }

  if (value instanceof FallbackProvider) {
    return prepareFallbackProvider(value);
  }

  if (value instanceof JsonRpcProvider) {
    return prepareJsonRpcProvider(value);
  }

  if (value instanceof Provider) {
    return prepareProvider(value);
  }

  if (typeof value === "object" && isNotNullNorUndefined(value)) {
    return mapValues(value, prepareToSerialize);
  }

  return value;
};

const prepareContract = (contract: Contract) => contract.address;

const prepareJsonRpcProvider = <T extends JsonRpcProvider>(
  jsonRpcProvider: T
) => jsonRpcProvider.network?.chainId + jsonRpcProvider.connection?.url;

const prepareFallbackProvider = (fallbackProvider: FallbackProvider) => {
  const chainId = String(fallbackProvider.network?.chainId);
  const urls = fallbackProvider.providerConfigs
    .map((config) => {
      if (config.provider instanceof JsonRpcProvider) {
        return config.provider.connection.url;
      }

      return "";
    })
    .join("|");

  return chainId + "|" + urls;
};

const prepareProvider = (provider: Provider) =>
  (provider as any).network?.chainId + (provider as any).connection?.url;
